import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  EmptyState, PageTitle, SectionAction, SectionCard, Skeleton, StatTile, StatusBadge,
} from '@lighthouse/design'
import {
  IconAlertCircle, IconBell, IconClock, IconCpu, IconFolder, IconLayoutDashboard, IconPlugConnected,
  IconServer2, IconServerOff,
} from '@tabler/icons-react'
import { useApiHealth } from '../hooks/useApiHealth'
import { useSourceHealth, useWorkerFleet } from '../hooks/useOverview'
import { useDeadLetterStats } from '../hooks/useQueues'
import { SLOW } from '../utils/polling'
import { NOT_MEASURED } from '../utils/format'
import { HealthGroupIcon, HealthLineRow } from '../components/health/HealthLineRow'
import { SignalDetectorSection } from '../components/signals/SignalDetectorSection'
import {
  NOT_CHECKED_ROWS, apiCheckRows, apiChecksTile, apiFooterText, connectorLines, deadLetterIntegrity,
  hostCards, lineTile, linkRowProps,
} from '../components/health/healthBoardFormat'
import type { HostBanner, HostCard, SummaryTile } from '../components/health/healthBoardFormat'
import { queryView, requestErrorText, sourcesLine, workersLine } from '../components/overview/overviewFormat'
import { dashInstruction, observedViaLabel } from '../components/sources/sourceFormat'
import type { SourceHealth } from '../types/observatory'

/**
 * Health Monitoring (Beacons) — a combined, per-host board of what is ALREADY measured.
 *
 * Decision 1, option (b), 2026-09-29: no new table, no new endpoint, no migration. It reads four
 * served readings and invents none:
 *
 *  - `/api/health` — the answering API instance's own checks. Anonymous, rerun on every request,
 *    and it names no host, so the section says it is one instance's view.
 *  - `api/observatory/workers` — Hangfire's server heartbeat, projected by Automaton.
 *  - `api/observatory/sources` — what each machine's connectors observed, one row per
 *    (connector, host).
 *  - `api/observatory/signals/summary` — whether the signal detector is reporting (one row,
 *    SignalDetectorSection). It is the store's own clock, not a new check.
 *
 * The rules it keeps:
 *
 *  1. **An unobserved thing is never green.** A stale projection withholds every worker's status; a
 *     group the API answer carries no entry for stays, as "Not checked by this instance"; a
 *     dead-letters "Healthy" over queues nobody could inspect is a dash.
 *  2. **No host-level status, no percentage, no combined verdict.** A host is not "healthy" because
 *     its worker beats, and a ratio of readings is not a ratio of reachability. The rows say what
 *     each feed measured and the reader combines them.
 *  3. **Loading, a failed request and an empty answer look different.** A failed `/api/health`
 *     request is "no answer", never "Unhealthy": the API may be fine and unreachable from here.
 *  4. **No added polling.** Every read is an existing hook on its existing key, so this page shares
 *     cache with the Overview and the rail badge: the dead-letter stats are the badge's query at the
 *     badge's interval.
 *
 * Every link goes through useNavigate; there is no href on this page.
 */
export function HealthMonitoringPage() {
  const navigate = useNavigate()
  const health = useApiHealth()
  const fleet = useWorkerFleet()
  const sources = useSourceHealth()
  const deadLetters = useDeadLetterStats({ refetchInterval: SLOW })

  const board = hostCards(fleet, sources)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 24 }}>
      <PageTitle subtitle={`${PAGE_SUBTITLE} — ${PAGE_DESCRIPTION}`}>Health Monitoring</PageTitle>

      {/* A. The summary band: three readings side by side. No percentage and no combined verdict. */}
      <SectionCard>
        <div
          data-health-summary=""
          className="lh-overview-grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
        >
          <SummaryStat label="API checks" tile={apiChecksTile(health, deadLetters)} />
          {/* Processes, not hosts: workersLine counts live Hangfire servers, and one machine can run several. */}
          <SummaryStat label="Worker processes" tile={lineTile(fleet, 'the worker fleet', workersLine)} />
          <SummaryStat label="Sources" tile={lineTile(sources, 'source health', sourcesLine)} />
        </div>
      </SectionCard>

      {/* B. This API instance. */}
      <ApiInstanceSection health={health} deadLetters={deadLetters} onOpen={navigate} />

      {/* B2. The signal detector — the one read of the signal store on this page, on the summary key the
          Overview tiles and the rail badge already poll. */}
      <SignalDetectorSection />

      {/* C. Automaton hosts. */}
      <SectionCard
        title="Automaton hosts"
        subtitle={
          'One card per machine name found in the worker heartbeat or the source readings. Worker rows '
          + "come from Hangfire's own server heartbeat; source rows from what that machine's connectors "
          + 'observed. The two are matched by machine name, ignoring case — nothing else links them.'
        }
        flush
      >
        <div data-health-section="hosts" style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* An empty board carries the banners in its own message; skeletons need none. */}
          {!board.loading && !board.empty && board.banners.map((banner) => <BannerLine key={banner.key} banner={banner} />)}
          {board.loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 2 }, (_, i) => <Skeleton key={i} height={72} />)}
            </div>
          ) : board.empty ? (
            <EmptyState
              icon={<IconServerOff size={28} stroke={1.5} />}
              title={board.empty.title}
              message={board.empty.message}
            />
          ) : (
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))' }}>
              {board.cards.map((card) => (
                <HostCardView key={card.key} card={card} onOpen={navigate} />
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {/* D. By source, across hosts. */}
      <SectionCard
        title="By source, across hosts"
        subtitle="Read a source across hosts: one host failing where others succeed points at that host's network, not the source."
        action={<SectionAction onClick={() => navigate('/sources')}>Open Sources →</SectionAction>}
        flush
      >
        <div data-health-section="by-source">
          <BySource sources={sources} onOpen={() => navigate('/sources')} />
        </div>
      </SectionCard>

      {/* E. What nothing checks. */}
      <SectionCard title="Not checked" subtitle="Things Observatory can name that nothing reports on. A dash each, never a colour." flush>
        <div data-health-section="not-checked">
          {NOT_CHECKED_ROWS.map((row) => (
            <HealthLineRow
              key={row.key}
              rowKey={row.key}
              icon={NOT_CHECKED_ICONS[row.key]}
              name={row.name}
              detail={row.reason}
              status={null}
              statusLabel="Not checked"
            />
          ))}
        </div>
      </SectionCard>
    </div>
  )
}

const PAGE_SUBTITLE = 'Beacons'
const PAGE_DESCRIPTION = 'What each host reports about the things it depends on, and what nothing checks.'

const NOT_CHECKED_ICONS: Record<string, ReactNode> = {
  'automaton-health': <IconServer2 size={15} />,
  'hangfire-dashboard': <IconLayoutDashboard size={15} />,
  scheduler: <IconClock size={15} />,
  notifications: <IconBell size={15} />,
  storage: <IconFolder size={15} />,
}

/** A summary tile. A null status is a dash with its caption — the tile carries no badge then. */
function SummaryStat({ label, tile }: { label: string; tile: SummaryTile }) {
  return <StatTile label={label} value={tile.value} status={tile.status} sublabel={tile.sublabel || undefined} />
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '8px 16px 2px', fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--color-text-disabled)',
    }}
    >
      {children}
    </div>
  )
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <div style={{ padding: '6px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: count }, (_, i) => <Skeleton key={i} height={18} />)}
    </div>
  )
}

/* ---- B. This API instance ------------------------------------------------ */

function ApiInstanceSection({ health, deadLetters, onOpen }: {
  health: ReturnType<typeof useApiHealth>
  deadLetters: ReturnType<typeof useDeadLetterStats>
  onOpen: (path: string) => void
}) {
  const view = queryView(health)
  const report = view === 'ready' ? health.data! : null
  const entries = report ? Object.keys(report.entries ?? {}).length : 0

  return (
    <SectionCard
      title="This API instance"
      subtitle={
        'The API process that answered this request, reporting what it can reach. /api/health does not say '
        + 'which instance answered, so another instance, if the API is ever scaled out, is not shown here.'
      }
      flush
    >
      <div data-health-section="api">
        {view === 'error' ? (
          <HealthLineRow
            rowKey="api-health"
            icon={<IconAlertCircle size={15} />}
            name="API health checks"
            detail={requestErrorText('the API health report', health.error)}
            status={null}
            statusLabel="no answer"
          />
        ) : !report ? (
          <SkeletonRows count={3} />
        ) : entries === 0 ? (
          <HealthLineRow
            rowKey="api-health"
            icon={<IconAlertCircle size={15} />}
            name="API health checks"
            detail="The API reported no checks."
            status={null}
            statusLabel="no checks"
          />
        ) : (
          apiCheckRows(report).map((checked) => {
            const row = deadLetterIntegrity(checked, deadLetters)
            const to = row.to
            const meta = row.checks.length > 0
              ? `${row.checks.join(', ')} · slowest ${row.slowest ?? NOT_MEASURED}`
              : null
            return (
              <HealthLineRow
                key={row.key}
                rowKey={row.key}
                icon={<HealthGroupIcon groupKey={row.key} />}
                name={row.label}
                detail={[row.detail, meta].filter((part): part is string => Boolean(part)).join(' · ') || null}
                status={row.role}
                statusLabel={row.statusLabel}
                onOpen={to ? () => onOpen(to) : undefined}
              />
            )
          })
        )}
        {report && (
          <div
            data-health-footer=""
            style={{ padding: '8px 16px 12px', fontSize: 11, color: 'var(--color-text-secondary)', borderTop: '1px solid var(--rule-hairline)' }}
          >
            {apiFooterText(report, health.dataUpdatedAt)}
          </div>
        )}
      </div>
    </SectionCard>
  )
}

/* ---- C. Automaton hosts -------------------------------------------------- */

function BannerLine({ banner }: { banner: HostBanner }) {
  const tone = banner.kind === 'loading' ? 'var(--color-text-secondary)' : 'var(--status-warning-fg)'
  return (
    <div data-host-banner={banner.kind} data-feed={banner.feed} style={{ fontSize: 12, color: tone }}>
      {banner.text}
    </div>
  )
}

/**
 * One machine. The header is its name and nothing else: there is no host-level status, by design
 * (rule 2 in the page's header). The rows below it carry every status on the card.
 */
function HostCardView({ card, onOpen }: { card: HostCard; onOpen: (path: string) => void }) {
  return (
    <div
      data-host-card={card.key}
      style={{ border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-bg-card)', minWidth: 0 }}
    >
      <div
        data-host-card-header=""
        style={{ padding: '10px 16px 4px', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}
      >
        {card.hostName}
      </div>

      {card.workers.length > 0 && (
        <>
          <SubLabel>Worker processes</SubLabel>
          {card.workers.map((worker) => (
            <HealthLineRow
              key={worker.key}
              rowKey={`worker:${worker.key}`}
              icon={<IconCpu size={15} />}
              name={worker.name}
              detail={worker.line.detail}
              status={worker.line.status}
              statusLabel={worker.line.label}
              onOpen={() => onOpen('/workers')}
            />
          ))}
        </>
      )}

      {card.sources.length > 0 && (
        <>
          <SubLabel>Source readings</SubLabel>
          {card.sources.map((source) => (
            <HealthLineRow
              key={`${source.connectorId}@${source.hostName}`}
              rowKey={`source:${source.connectorId}`}
              icon={<IconPlugConnected size={15} />}
              name={`${source.displayName} (${source.connectorId})`}
              detail={sourceDetail(source)}
              status={source.status ?? null}
              statusLabel={source.statusLabel}
              onOpen={() => onOpen('/sources')}
            />
          ))}
        </>
      )}

      {card.notes.length > 0 && (
        <div style={{ padding: '6px 16px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {card.notes.map((note) => (
            <span key={note} data-host-note="" style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{note}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * A source row's detail: the server's sentence, what it rests on, and — for a dash — the
 * instruction behind it, which differs between a stale observer and a source nobody asked.
 */
function sourceDetail(source: SourceHealth): string {
  const instruction = dashInstruction(source.reachability, source.isProjectionStale)
  return [source.detail, observedViaLabel(source.observedVia), instruction]
    .filter((part): part is string => Boolean(part))
    .join(' · ')
}

/* ---- D. By source, across hosts ------------------------------------------ */

function BySource({ sources, onOpen }: { sources: ReturnType<typeof useSourceHealth>; onOpen: () => void }) {
  const view = queryView(sources)
  if (view === 'error') {
    return (
      <HealthLineRow
        rowKey="sources"
        icon={<IconAlertCircle size={15} />}
        name="Source readings"
        detail={requestErrorText('source health', sources.error)}
        status={null}
        statusLabel="no answer"
      />
    )
  }
  if (view === 'loading') return <SkeletonRows count={2} />

  const overview = sources.data!
  if (overview.hasNeverReported || overview.sources.length === 0) {
    const line = sourcesLine(overview)
    return (
      <HealthLineRow
        rowKey="sources"
        icon={<IconPlugConnected size={15} />}
        name="Source readings"
        detail={line.detail}
        status={null}
        statusLabel={line.label}
      />
    )
  }

  return (
    <>
      {overview.isProjectionStale && (
        <div data-host-banner="stale" style={{ padding: '8px 16px 0', fontSize: 12, color: 'var(--status-warning-fg)' }}>
          {sourcesLine(overview).detail}
        </div>
      )}
      {connectorLines(overview).map((line) => (
        <div
          key={line.connectorId}
          className="lh-health-row"
          data-connector={line.connectorId}
          {...linkRowProps(onOpen, `${line.displayName}: open Sources`)}
          style={{ flexWrap: 'wrap', cursor: 'pointer' }}
        >
          <span className="lh-health-icon"><IconPlugConnected size={15} /></span>
          <span className="lh-health-name" style={{ flex: '1 1 160px' }}>
            {line.displayName} <span style={{ color: 'var(--color-text-disabled)' }}>({line.connectorId})</span>
          </span>
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>
            {line.chips.map((chip) => (
              <span
                key={chip.key}
                data-connector-chip={chip.hostName}
                data-status={chip.status ?? 'unmeasured'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, minWidth: 0 }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{chip.hostName}</span>
                {chip.status ? (
                  <StatusBadge status={chip.status} label={chip.statusLabel} />
                ) : (
                  <span style={{ color: 'var(--color-text-disabled)', whiteSpace: 'nowrap' }}>
                    <span aria-hidden="true">{NOT_MEASURED}</span> {chip.statusLabel}
                  </span>
                )}
              </span>
            ))}
          </span>
        </div>
      ))}
    </>
  )
}
